import React, { Key, useCallback } from 'react';
import { QStack } from '@qualio/ui-components';
import Card from '../Card/Card';
import * as DisplayStrings from '../../displayStrings';

type CardListProps<
  ItemType extends {
    uniqueId: Key;
    type: string;
    workflow_field_id?: number | null;
    label: string;
  },
> = {
  items: readonly ItemType[];
  removeItem: (item: ItemType, index: number) => void;
  moveItem: (item: ItemType, currentIndex: number, newIndex: number) => void;
  getCardTitle?: (item: ItemType) => React.ReactNode;
  renderItem: (item: ItemType, index: number, fullArray: readonly ItemType[]) => React.ReactNode;
};

const CardList = <T extends { uniqueId: Key; type: string; workflow_field_id?: number | null; label: string }>(
  props: CardListProps<T>,
) => {
  const { items: itemArray, renderItem, getCardTitle = () => undefined, removeItem, moveItem } = props;

  const onItemMove = useCallback(
    (direction: 'up' | 'down', item: T, index: number, fullArray: readonly T[]) => {
      if (item.type === 'section' && !!item.workflow_field_id) {
        const risk = item;
        const severity = fullArray[index + 1]!;
        const probability = fullArray[index + 2]!;

        if (direction === 'up') {
          moveItem(risk, index, index - 1);
          moveItem(severity, index + 1, index);
          moveItem(probability, index + 2, index + 1);
        } else {
          moveItem(probability, index + 2, index + 3);
          moveItem(severity, index + 1, index + 2);
          moveItem(risk, index, index + 1);
        }
      } else if (
        fullArray[index - 3]?.type === 'section' &&
        !!fullArray[index - 3]?.workflow_field_id &&
        direction === 'up'
      ) {
        moveItem(item, index, index - 3);
      } else if (
        fullArray[index + 1]?.type === 'section' &&
        !!fullArray[index + 1]?.workflow_field_id &&
        direction === 'down'
      ) {
        moveItem(item, index, index + 3);
      } else {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        moveItem(item, index, newIndex);
      }
    },
    [moveItem],
  );
  return (
    <QStack spacing={6}>
      {itemArray.map((item, index, fullArray) => (
        <Card
          key={item.uniqueId}
          title={getCardTitle(item)}
          data-cy={`card-item-${item.type}-${index}`}
          includeControls={true}
          onMove={(direction) => onItemMove(direction, item, index, fullArray)}
          onDelete={() => removeItem(item, index)}
          disableUp={index === 0}
          disableDown={
            index === itemArray.length - 1 ||
            (item.type === 'section' && !!item.workflow_field_id && index === itemArray.length - 3)
          }
          disabledDeleteTooltip={item.workflow_field_id ? DisplayStrings.DisabledDeleteTooltip(item.label) : undefined}
          isHidden={item.type === 'dropdown' && !!item.workflow_field_id}
        >
          {renderItem(item, index, fullArray)}
        </Card>
      ))}
    </QStack>
  );
};

export default CardList;
